import dayjs from 'dayjs';
import type { Colors } from '../../constants/theme';
import type { ILocation } from '../../types/global/helper';
import { BRAND_LUXE_PRIMARY, BRAND_OUTLINE_VARIANT, BRAND_SURFACE_CONTAINER_HIGHEST } from './brandDashboardTokens';
import TheaterComedyOutlined from '@mui/icons-material/TheaterComedyOutlined';
import {
    DevicesOutlined,
    FitnessCenterOutlined,
    RestaurantOutlined,
    FaceOutlined,
    CheckroomOutlined,
    SportsEsportsOutlined,
    SchoolOutlined,
    PetsOutlined,
    TravelExploreOutlined,
    MonetizationOnOutlined,
    DirectionsCarOutlined,
    WeekendOutlined,
    FamilyRestroomOutlined,
    PaletteOutlined,
    MusicNoteOutlined,
    MenuBookOutlined,
    EnergySavingsLeafOutlined,
    SelfImprovementOutlined,
    LayersOutlined,
    HomeRepairServiceOutlined,
    BusinessCenterOutlined,
    FavoriteOutlined,
    SportsSoccerOutlined,
    NewspaperOutlined,
    MovieOutlined,
    YardOutlined,
    NightlifeOutlined,
    ChurchOutlined,
    PhotoCameraOutlined,
    SpaOutlined,
    DiamondOutlined,
    LocationCityOutlined,
} from '@mui/icons-material';
import { NICHE_MAIN_ORDER, NICHE_MAIN_ALPHABETICAL, NICHE_SUBS_BY_MAIN } from './ugcCampaignCategorySchema';

export {
    getSubcategoriesForMain,
    isValidSubForMain,
    isValidSubcategory,
    isNicheMainCategory,
    findMainCategoryForSub,
    NICHE_MAIN_ORDER,
    NICHE_MAIN_ALPHABETICAL,
    NICHE_SUBS_BY_MAIN,
    ALL_SUBCATEGORIES_SORTED,
} from './ugcCampaignCategorySchema';
export type { NicheMainCategory } from './ugcCampaignCategorySchema';

type NicheIcon = typeof CheckroomOutlined;

const NICHE_ICONS: Record<string, NicheIcon> = {
    Fashion: CheckroomOutlined,
    'Beauty & Skincare': FaceOutlined,
    'Fitness & Health': FitnessCenterOutlined,
    Lifestyle: WeekendOutlined,
    'Travel & Adventure': TravelExploreOutlined,
    'Food & Cooking': RestaurantOutlined,
    'Technology & Gadgets': DevicesOutlined,
    Gaming: SportsEsportsOutlined,
    'Parenting & Family': FamilyRestroomOutlined,
    'Education & Career': SchoolOutlined,
    'Art & Design': PaletteOutlined,
    'Music & Performance': MusicNoteOutlined,
    'Finance & Investing': MonetizationOnOutlined,
    'Comedy & Entertainment': TheaterComedyOutlined,
    Automotive: DirectionsCarOutlined,
    'Book Recommendations': MenuBookOutlined,
    'Sustainable Living': EnergySavingsLeafOutlined,
    'Pet Influencers': PetsOutlined,
    'Spirituality & Mindfulness': SelfImprovementOutlined,
    Minimalism: LayersOutlined,
    'DIY Home Renovation': HomeRepairServiceOutlined,
    'Business & Entrepreneurship': BusinessCenterOutlined,
    'Relationships & Dating': FavoriteOutlined,
    Sports: SportsSoccerOutlined,
    'News & Commentary': NewspaperOutlined,
    'Movies & Television': MovieOutlined,
    'Home & Garden': YardOutlined,
    'Events & Nightlife': NightlifeOutlined,
    'Faith & Religion': ChurchOutlined,
    'Photography & Videography': PhotoCameraOutlined,
    'Wellness & Self-Care': SpaOutlined,
    'Luxury & Premium Lifestyle': DiamondOutlined,
    'Local Culture & Community': LocationCityOutlined,
};

export function iconForNicheMain(main: string): NicheIcon {
    return NICHE_ICONS[main] || PaletteOutlined;
}

/** Main category options — `key` and `label` match backend `category` enum strings. */
export const NICHE_OPTIONS = NICHE_MAIN_ORDER.map(main => ({
    key: main,
    label: main,
    Icon: iconForNicheMain(main),
}));

/** Main categories with icons, A–Z by label (for quick picks + search UI). */
export const NICHE_OPTIONS_ALPHABETICAL = [...NICHE_OPTIONS].sort((a, b) => a.label.localeCompare(b.label));

export function buildNicheOptions(mains: string[]) {
    return mains.map(main => ({
        key: main,
        label: main,
        Icon: iconForNicheMain(main),
    }));
}

export function buildNicheOptionsAlphabetical(mains: string[]) {
    return buildNicheOptions(mains).sort((a, b) => a.label.localeCompare(b.label));
}

/** Number of main-category shortcut chips before using search. */
export const MAIN_CATEGORY_QUICK_PICK = 5;

export const PLATFORM_OPTIONS = ['instagram', 'tiktok', 'youtube', 'facebook', 'thspian', 'x', 'reddit'];

export const INFLUENCER_TIERS: { id: string; name: string; reach: string }[] = [
    { id: 'micro', name: 'Micro', reach: '300–50k' },
    { id: 'mid', name: 'Mid-tier', reach: '50k–200k' },
    { id: 'macro', name: 'Macro', reach: '200k+' },
    { id: 'top-tier', name: 'Top-tier', reach: '1M+' },
];

export const REWARD_OPTIONS: { value: 'paid' | 'gifted' | 'both'; label: string }[] = [
    { value: 'paid', label: 'Paid' },
    { value: 'gifted', label: 'Product only' },
    { value: 'both', label: 'Paid + product' },
];

export const TOTAL_STEPS = 3;
export const STEP_LABELS = ['Campaign Identity', 'Budget & Targeting', 'Details & Launch'];

/* ── Field styling ─────────────────────────────────── */

export function getFieldSx(t: Colors) {
    return {
        '& .MuiOutlinedInput-root': {
            bgcolor: '#262626',
            borderRadius: 2,
            color: t.primary_white,
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
        },
        '& .MuiInputBase-input': { color: t.primary_white },
        '& .MuiInputBase-input::placeholder': { color: t.form_label, opacity: 1 },
    };
}

/** Budget step — light grey border at rest, gold focus ring, softer placeholder. */
export function getUgcBudgetFieldSx(t: Colors) {
    return {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: t.primary_white,
            '& fieldset': {
                border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255,255,255,0.32)',
            },
            '&.Mui-focused fieldset': {
                borderColor: BRAND_LUXE_PRIMARY,
                borderWidth: 1,
            },
            '&.Mui-focused': {
                boxShadow: `0 0 0 1px ${BRAND_LUXE_PRIMARY}59`,
            },
        },
        '& .MuiInputBase-input': { color: t.primary_white },
        '& .MuiInputBase-input::placeholder': {
            color: t.form_label,
            opacity: 0.45,
        },
    };
}

export const datePickerSx = {
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#262626',
    '&:focus-within': {
        border: 'none',
        boxShadow: `0 0 0 1px ${BRAND_LUXE_PRIMARY}59`,
    },
};

/** Budget step delivery date — visible grey border before focus. */
export const ugcBudgetDatePickerSx = {
    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
    borderRadius: '8px',
    backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
    '&:hover': {
        borderColor: 'rgba(255,255,255,0.32)',
    },
    '&:focus-within': {
        border: `1px solid ${BRAND_LUXE_PRIMARY}`,
        boxShadow: `0 0 0 1px ${BRAND_LUXE_PRIMARY}59`,
    },
};

export function parseBudgetInput(raw: string): number {
    const n = Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
}

/** Normalize form/API location into the campaign PATCH body shape. Returns undefined when empty. */
export function serializeCampaignLocation(loc?: Pick<ILocation, 'address' | 'description' | 'coordinates'> | null) {
    if (!loc?.address?.trim()) return undefined;
    const coords = loc.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return undefined;
    if (coords[0] === 0 && coords[1] === 0) return undefined;
    return {
        type: 'Point' as const,
        coordinates: coords,
        address: loc.address.trim(),
        description: loc.description?.trim() || loc.address.trim(),
    };
}

export function campaignLocationToForm(
    loc?: Pick<ILocation, 'address' | 'description' | 'coordinates' | 'short_name' | 'type'> | null,
): ILocation {
    if (!loc?.address?.trim()) {
        return { type: 'Point', coordinates: [], address: '', description: '', short_name: '' };
    }
    return {
        type: loc.type || 'Point',
        coordinates: Array.isArray(loc.coordinates) ? loc.coordinates : [],
        address: loc.address,
        description: loc.description || loc.address,
        short_name: loc.short_name || loc.address.split(',')[0]?.trim() || loc.address,
    };
}

/** Validates a public http(s) link for clipping source footage. */
export function isValidClippingAssetUrl(url: string): boolean {
    const trimmed = url.trim();
    if (!trimmed) return false;
    try {
        const parsed = new URL(trimmed);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/* ── Dev auto-fill helpers ─────────────────────────── */

const pick = <T>(arr: readonly T[] | T[]): T => arr[Math.floor(Math.random() * arr.length)];
const pickN = <T>(arr: T[], min: number, max: number): T[] => {
    const n = min + Math.floor(Math.random() * (max - min + 1));
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, n);
};
const randInt = (min: number, max: number) => min + Math.floor(Math.random() * (max - min + 1));
const futureDate = (minDays: number, maxDays: number) =>
    dayjs().add(randInt(minDays, maxDays), 'day').format('YYYY-MM-DDTHH:mm:ssZ');

const TITLES = [
    'Summer Glow Collection Launch',
    'Urban Streetwear Drop 2026',
    'Holiday Gift Guide Campaign',
    'Fitness Challenge Series',
    'Tech Unboxing Showcase',
    'Beauty Secrets Reveal',
    'Midnight Luxe Collaboration',
    'Eco-Friendly Living Series',
];

const DESCRIPTIONS = [
    'We need authentic creators to showcase our latest collection through lifestyle-driven content that resonates with a modern audience. Think candid moments, real environments, and genuine reactions.',
    'Looking for energetic creators to produce product reviews and unboxing videos. High production value preferred, but authenticity is key. Show the product in your daily routine.',
    'Create engaging short-form content highlighting our brand values of sustainability and style. We want content that sparks conversation and drives community engagement.',
    'Partner with us to tell a compelling story around our new product line. We are looking for bold, creative takes that stand out in feeds and inspire action.',
];

export function autoFillStep0() {
    const category = pick(NICHE_MAIN_ALPHABETICAL);
    const subs = [...NICHE_SUBS_BY_MAIN[category]];
    const sub_category = pick(subs);
    return {
        title: pick(TITLES),
        description: pick(DESCRIPTIONS),
        category,
        sub_category,
    };
}

export function autoFillStep1() {
    const startDate = futureDate(7, 30);
    const endDate = dayjs(startDate).add(randInt(14, 90), 'day').format('YYYY-MM-DDTHH:mm:ssZ');
    return {
        budget: String(randInt(1, 50) * 1000),
        publishDate: endDate,
        reward: pick<string>(['paid', 'gifted', 'both']),
        platforms: pickN([...PLATFORM_OPTIONS], 1, 3),
        tier: pick(INFLUENCER_TIERS.map(t => t.id)),
        participants: randInt(1, 15),
        startDate,
        endDate,
    };
}

export const DEFAULT_PRODUCTION_REQUIREMENTS = [
    'Use a handheld or lavalier microphone.',
    'Shoot on a smartphone or DSLR.',
    'Use a lighting source, or film in a well-lit space.',
    'Video must be sharp — not grainy or unusable.',
    'Audio must be clean and intelligible.',
];

export function autoFillStep2() {
    const objectivePool = [
        'Increase brand awareness among Gen Z audience',
        'Drive product page visits and conversions',
        'Build authentic social proof through UGC',
        'Grow Instagram following by 15%',
        'Generate 50+ pieces of reusable content',
    ];
    const requirementPool = [
        'Minimum 10k followers on primary platform',
        'Must be 18+ years old',
        'Prior brand collaboration experience preferred',
        'Ability to produce high-quality vertical video',
        'Active engagement rate above 3%',
    ];
    const deliverablePool = [
        { type: 'instagram_reel', platform: 'instagram', duration_range: '15-30s', count: 1 },
        { type: 'instagram_story', platform: 'instagram', duration_range: '0-15s', count: 1 },
        { type: 'tiktok_video', platform: 'tiktok', duration_range: '15-30s', count: 1 },
        { type: 'instagram_post', platform: 'instagram', duration_range: 'n/a', count: 1 },
        { type: 'ugc_video', platform: 'instagram', duration_range: '30s-1min', count: 1 },
    ];
    const productPool = [
        'Premium Starter Kit',
        'Limited Edition Gift Box',
        'Full-Size Product Bundle',
        'Exclusive Sample Set',
    ];
    const ideasPool = [
        'Outfit check for the event',
        'Day-in-the-life vlog leading up to launch',
        'Dance trend to a featured song',
        'Unboxing and first impressions',
        'Get-ready-with-me featuring the product',
    ];
    return {
        description: pick(DESCRIPTIONS),
        objectives: pickN(objectivePool, 2, 4),
        requirements: pickN(requirementPool, 2, 3),
        ideas: pickN(ideasPool, 2, 4),
        deliverables: pickN(deliverablePool, 2, 4),
        products: pickN(productPool, 1, 2),
        physicalProduct: Math.random() > 0.5,
        exclusiveRights: Math.random() > 0.3,
    };
}
